import React from "react";

import type { ActivityContentTaskDescriptionChange } from "@/api";
import type { Activity } from "@/models/activities";
import type { ActivityHandler } from "../interfaces";
import { feedTitle, taskLink } from "../feedItemLinks";
import { Summary } from "turboui";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";

const TaskDescriptionChange: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity): string {
    const { task, space } = content(activity);
    const isSpaceTask = task?.type === "пространство";

    if (isSpaceTask) {
      return space ? paths.spaceKanbanPath(space.id, { taskId: task.id }) : paths.homePath();
    } else {
      return task ? paths.taskPath(task.id) : paths.homePath();
    }
  },

  PageTitle(_props: { activity: any }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page }: { activity: Activity; page: any }) {
    const { task, projectName, space } = content(activity);
    const isSpaceTask = task?.type === "пространство";

    // Determine link and context
    const link = isSpaceTask
      ? space
        ? taskLink(task, { spaceId: space.id })
        : task.name
      : task
        ? taskLink(task)
        : "a task";

    // Add context suffix based on page view
    const shouldShowContext = (isSpaceTask && page !== "пространство") || (!isSpaceTask && page !== "проект");
    const context = shouldShowContext ? (isSpaceTask ? space?.name : projectName) : undefined;

    return context
      ? feedTitle(activity, "обновил(а) описание", link, "в", context)
      : feedTitle(activity, "обновил(а) описание", link);
  },

  FeedItemContent({ activity }: { activity: Activity }) {
    const data = content(activity);
    const { mentionedPersonLookup } = useRichEditorHandlers();

    const rawDescription = data.description ?? data.task?.description;

    if (!rawDescription) return null;

    const description = typeof rawDescription === "string" ? JSON.parse(rawDescription) : rawDescription;

    return <Summary content={description} characterCount={200} mentionedPersonLookup={mentionedPersonLookup} />;
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-start";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    const { task } = content(activity);

    return "Обновлено описание: " + (task?.name || "a task");
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    return content(activity).projectName;
  },
};

function content(activity: Activity): ActivityContentTaskDescriptionChange {
  return activity.content as ActivityContentTaskDescriptionChange;
}

export default TaskDescriptionChange;
